import React, { useState } from 'react';
import { NavLink, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import Tooltip from './Tooltip';

const NAV = {
  admin: [
    { to:'/dashboard',    icon:'⊞', label:'Dashboard',    tip:'Admin overview — manage all organizations' },
    { to:'/manage/exams', icon:'◉', label:'Manage Exams', tip:'View all exams across all organizations' },
  ],
  organization: [
    { to:'/dashboard',    icon:'⊞', label:'Overview',  tip:'Dashboard — stats, instructors, students, groups' },
    { to:'/manage/exams', icon:'◉', label:'Exams',     tip:'Create, edit, publish and monitor exams' },
    { to:'/exams/create', icon:'＋', label:'New Exam',  tip:'Create a new exam' },
  ],
  instructor: [
    { to:'/dashboard',    icon:'⊞', label:'Dashboard',    tip:'Your live exam sessions and assignments' },
    { to:'/manage/exams', icon:'◉', label:'Manage Exams', tip:'Exams assigned to you — open to students, monitor live' },
  ],
  student: [
    { to:'/dashboard', icon:'⊞', label:'Dashboard', tip:'Your enrolled exams, results and notifications' },
  ],
};

const ROLE_COLOR = {
  admin:        'var(--accent-danger)',
  organization: 'var(--accent-secondary)',
  instructor:   'var(--accent-primary)',
  student:      'var(--accent-success)',
};

function OrgBrand({ orgId, orgName }) {
  const [logo, setLogo] = React.useState(null);
  React.useEffect(() => {
    if (!orgId) return;
    import('axios').then(({ default: ax }) => {
      ax.get(`/api/organizations/${orgId}`)
        .then(r => setLogo(r.data?.logo_url))
        .catch(() => {});
    });
  }, [orgId]);
  return (
    <div style={{ padding:'8px 20px 12px', borderBottom:'1px solid var(--border)', display:'flex', alignItems:'center', gap:10 }}>
      {logo && <img src={logo} alt="org logo" style={{ height:28, width:28, borderRadius:6, objectFit:'contain', background:'#fff', border:'1px solid var(--border)', padding:2 }} />}
      <div style={{ fontSize:12, color:'var(--text-secondary)', overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>🏢 {orgName}</div>
    </div>
  );
}

function LogoutModal({ onConfirm, onCancel }) {
  React.useEffect(() => {
    const fn = (e) => { if (e.key === 'Escape') onCancel(); };
    window.addEventListener('keydown', fn);
    return () => window.removeEventListener('keydown', fn);
  }, [onCancel]);

  return (
    <div onClick={(e) => e.target === e.currentTarget && onCancel()} style={{ position:'fixed', inset:0, zIndex:10000, background:'rgba(0,0,0,0.55)', backdropFilter:'blur(4px)', display:'flex', alignItems:'center', justifyContent:'center', padding:20 }}>
      <div style={{ background:'var(--bg-card)', border:'1px solid var(--border)', borderRadius:14, maxWidth:360, width:'100%', overflow:'hidden', boxShadow:'0 20px 60px rgba(0,0,0,0.4)', animation:'sidebarLogout 0.18s ease' }}>
        <div style={{ padding:'16px 20px', background:'rgba(245,158,11,0.08)', borderBottom:'1px solid rgba(245,158,11,0.2)', display:'flex', alignItems:'center', gap:12 }}>
          <span style={{ fontSize:26 }}>👋</span>
          <div style={{ fontWeight:700, fontSize:16, color:'var(--accent-warning)' }}>Sign Out?</div>
        </div>
        <div style={{ padding:'18px 20px' }}>
          <p style={{ fontSize:14, color:'var(--text-secondary)', marginBottom:16, lineHeight:1.6 }}>
            You are about to sign out of ExamAI.
          </p>
          <div style={{ background:'rgba(245,158,11,0.06)', border:'1px solid rgba(245,158,11,0.15)', borderRadius:8, padding:'12px 14px', marginBottom:16 }}>
            <div style={{ fontSize:11, fontWeight:700, color:'var(--accent-warning)', textTransform:'uppercase', letterSpacing:'0.7px', marginBottom:8 }}>📋 What will happen</div>
            {[
              'Your active session will end immediately',
              'You will be redirected to the login page',
              'Any unsaved work on this page will be lost',
            ].map((item, i) => (
              <div key={i} style={{ display:'flex', gap:8, alignItems:'flex-start', marginBottom: i < 2 ? 5 : 0 }}>
                <span style={{ color:'var(--accent-warning)', flexShrink:0, marginTop:2 }}>•</span>
                <span style={{ fontSize:13, color:'var(--text-secondary)', lineHeight:1.5 }}>{item}</span>
              </div>
            ))}
          </div>
          <div style={{ display:'flex', gap:10, justifyContent:'flex-end' }}>
            <button className="btn btn-secondary" onClick={onCancel} autoFocus>Stay Logged In</button>
            <button className="btn btn-warning" onClick={onConfirm}>Sign Out</button>
          </div>
        </div>
      </div>
      <style>{`@keyframes sidebarLogout{from{opacity:0;transform:translateY(10px) scale(.97)}to{opacity:1;transform:none}}`}</style>
    </div>
  );
}

export default function Sidebar() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [showLogout, setShowLogout] = useState(false);
  const nav = NAV[user?.role] || NAV.student;
  const roleColor = ROLE_COLOR[user?.role] || 'var(--accent-primary)';

  const doLogout = () => { setShowLogout(false); logout(); navigate('/login'); };

  return (
    <>
      <div className="sidebar">
        <div className="sidebar-logo">
          <div className="logo-text">⬡ ExamAI</div>
          <div className="logo-sub">PROCTORED ASSESSMENTS</div>
        </div>

        {user?.org_name && <OrgBrand orgId={user.org_id} orgName={user.org_name} />}

        <nav>
          {nav.map(item => (
            <Tooltip key={item.to} text={item.tip} placement="right">
              <NavLink to={item.to} end className={({ isActive }) => `nav-item${isActive?' active':''}`}>
                <span className="nav-icon">{item.icon}</span>
                {item.label}
              </NavLink>
            </Tooltip>
          ))}
        </nav>

        <div className="sidebar-footer">
          <div className="user-chip">
            <div className="user-avatar" style={{ background:`linear-gradient(135deg, ${roleColor}, var(--accent-secondary))`, flexShrink:0 }}>
              {user?.full_name?.[0]?.toUpperCase()}
            </div>
            <div style={{ flex:1, minWidth:0 }}>
              <div className="user-name" style={{ overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>{user?.full_name}</div>
              <div className="user-role" style={{ color:roleColor, fontWeight:600, fontSize:10 }}>{user?.role?.toUpperCase()}</div>
            </div>
            <Tooltip text="Sign out of ExamAI" placement="top">
              <button onClick={() => setShowLogout(true)}
                style={{ background:'none', border:'none', cursor:'pointer', color:'var(--text-muted)', fontSize:16, padding:'4px 6px', borderRadius:4, transition:'color 0.15s', flexShrink:0 }}
                aria-label="Logout">⇥</button>
            </Tooltip>
          </div>
        </div>
      </div>

      {showLogout && <LogoutModal onConfirm={doLogout} onCancel={() => setShowLogout(false)} />}
    </>
  );
}
